import React from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  LinearProgress,
  Alert,
  Divider,
  Paper
} from '@mui/material'
import {
  TrendingUp,
  Schedule,
  CheckCircle,
  Warning,
  Person,
  Assignment
} from '@mui/icons-material'
import { format } from 'date-fns'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts'

const ClosureDashboard = ({ data, loading }) => {
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading dashboard data...
        </Typography>
      </Box>
    )
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No dashboard data available
        </Alert>
      </Box>
    )
  }

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8']

  return (
    <Box sx={{ p: 3 }}>
      {/* Recent Activity */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Recently Closed Files */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader 
              title="Recently Closed Files"
              subheader="Last 10 closures"
              avatar={<CheckCircle color="success" />}
            />
            <CardContent>
              {data.recentClosures?.length > 0 ? (
                <List dense>
                  {data.recentClosures.slice(0, 5).map((closure, index) => (
                    <React.Fragment key={closure._id}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={600}>
                              {closure.mrdFile?.fileNumber} - {closure.mrdFile?.patientName}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              Closed by {closure.closedBy?.name} • {format(new Date(closure.closedAt), 'MMM dd, HH:mm')}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Chip
                            label={closure.closureReason?.replace(/_/g, ' ') || 'N/A'}
                            size="small"
                            variant="outlined"
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < 4 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  No recent closures
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Files */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader 
              title="Files with Incomplete Checklist"
              subheader="Pending closure items"
              avatar={<Warning color="warning" />}
            />
            <CardContent>
              {data.pendingFiles?.length > 0 ? (
                <List dense>
                  {data.pendingFiles.slice(0, 5).map((file, index) => (
                    <React.Fragment key={file._id}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={600}>
                              {file.mrdFile?.fileNumber} - {file.mrdFile?.patientName}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              Admission: {file.mrdFile?.admissionDate ? format(new Date(file.mrdFile.admissionDate), 'MMM dd, yyyy') : 'N/A'}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Chip
                            label="Incomplete"
                            size="small"
                            color="warning"
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < 4 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  No pending files
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Closure Trend */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Daily Closure Trend (Last 7 Days)
            </Typography>
            {data.closureTrend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.closureTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#8884d8" 
                    name="Closures"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                No trend data available
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Closure by Reason */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Closure by Reason
            </Typography>
            {data.byReason?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.byReason}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ _id, count }) => `${_id?.replace(/_/g, ' ') || 'Other'}: ${count}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {data.byReason.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                No reason data available
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Staff Performance */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="Top Performers (This Month)"
              subheader="Staff with most closures"
              avatar={<Person color="primary" />}
            />
            <CardContent>
              {data.staffPerformance?.length > 0 ? (
                <List>
                  {data.staffPerformance.map((staff, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={600}>
                              {staff.staff?.name || 'Unknown'}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {staff.staff?.designation} • {staff.staff?.email}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Chip
                            label={`${staff.closures} closures`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < data.staffPerformance.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  No performance data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="Quick Actions"
              subheader="Common closure tasks"
              avatar={<Assignment color="info" />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>{data.summary?.readyForClosure || 0}</strong> files are ready for closure
                    </Typography>
                  </Alert>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2">
                      Closure Completion Rate
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {data.summary?.totalClosures > 0 ? 
                        Math.round((data.summary.recentClosures / data.summary.totalClosures) * 100) : 0}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={data.summary?.totalClosures > 0 ? 
                      (data.summary.recentClosures / data.summary.totalClosures) * 100 : 0}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Tip:</strong> Files with complete verification checklists can be closed immediately. 
                    Review pending items to expedite the closure process.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default ClosureDashboard